import { addBreadcrumb } from '@sentry/angular';
import { take, filter, map } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import {
  applyActionCode,
  Auth,
  checkActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
  authState,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  User,
  type UserCredential,
} from '@angular/fire/auth';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { damoAuthConfig } from '../../../damoauth.config';
import { AuthError } from '../errors/auth.error';
import { FirebaseError } from 'firebase/app';
import type { ActionCodeInfo } from 'firebase/auth';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly auth = inject(Auth);
  private readonly http = inject(HttpClient);
  private readonly config = inject(damoAuthConfig);

  readonly authState$: Observable<User | null> = authState(this.auth);

  readonly currentUser$ = this.authState$.pipe(
    filter((user): user is User => user !== undefined && user !== null),
    map((user) => user),
  );

  async isUserAuthenticated(): Promise<boolean> {
    return firstValueFrom(
      this.authState$.pipe(
        filter((user) => user !== undefined),
        take(1),
        map((user) => !!user),
      ),
    );
  }

  async signIn(email: string, password: string): Promise<User> {
    try {
      const userCredential: UserCredential = await signInWithEmailAndPassword(this.auth, email, password);
      return userCredential.user;
    } catch (err: unknown) {
      if ((err as { code?: string })?.code === 'auth/multi-factor-auth-required') {
        throw err;
      }
      if (err instanceof FirebaseError) {
        throw new AuthError(err.code);
      }
      addBreadcrumb({ message: `Unexpected error during sign-in: ${(err as Error).message}`, level: 'error' });
      throw new AuthError();
    }
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error: unknown) {
      addBreadcrumb({ message: `Failed to send password reset email: ${(error as Error).message}`, level: 'error' });
      throw new AuthError();
    }
  }

  async signOut(): Promise<void> {
    try {
      await signOut(this.auth);
    } catch (error: unknown) {
      addBreadcrumb({ message: `Failed to sign out: ${(error as Error).message}`, level: 'error' });
      throw new AuthError();
    }
  }

  async setSessionToken(user: User): Promise<void> {
    const idToken = await user.getIdToken();
    try {
      await firstValueFrom(this.http.post(`${this.config.fns}/utils-session/login`, { sessionToken: idToken }, { withCredentials: true }));
    } catch (err: unknown) {
      if (err instanceof HttpErrorResponse) {
        throw new AuthError(err.error.code);
      }
      addBreadcrumb({ message: `Failed to set session token: ${(err as Error).message}`, level: 'error' });
      throw new AuthError();
    }
  }

  async checkActionCode(actionCode: string): Promise<ActionCodeInfo> {
    try {
      return await checkActionCode(this.auth, actionCode);
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      throw new AuthError();
    }
  }

  async clearMfaEnabled(): Promise<void> {
    const idToken = await this.auth.currentUser?.getIdToken();
    if (!idToken) {
      throw new AuthError();
    }

    try {
      await firstValueFrom(this.http.post(`${this.config.fns}/stas-users/mfa/totp/disable`, {}, { headers: { Authorization: `Bearer ${idToken}` } }));
    } catch (error: unknown) {
      if (error instanceof HttpErrorResponse) {
        throw new AuthError(error.error?.code);
      }
      throw new AuthError();
    }
  }

  async verifyPasswordResetCode(actionCode: string): Promise<string> {
    try {
      return await verifyPasswordResetCode(this.auth, actionCode);
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      addBreadcrumb({ message: `Failed to verify password reset code: ${(error as Error).message}`, level: 'error' });
      throw new AuthError();
    }
  }

  async confirmPasswordReset(actionCode: string, newPassword: string): Promise<void> {
    try {
      await confirmPasswordReset(this.auth, actionCode, newPassword);
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      addBreadcrumb({ message: `Failed to confirm password reset: ${(error as Error).message}`, level: 'error' });
      throw new AuthError();
    }
  }

  async applyActionCode(actionCode: string): Promise<void> {
    try {
      await applyActionCode(this.auth, actionCode);
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        throw new AuthError(error.code);
      }
      addBreadcrumb({ message: `Failed to apply action code: ${(error as Error).message}`, level: 'error' });
      throw new AuthError();
    }
  }

  async updateUserPassword(actionCode: string, newPassword: string): Promise<void> {
    addBreadcrumb({ message: 'Attempting to update user password', level: 'info' });
    try {
      await firstValueFrom(this.http.post(`${this.config.fnsOrigin}/stas-userResetPassword`, { data: { password: newPassword, oobCode: actionCode } }));
    } catch (error: unknown) {
      addBreadcrumb({ message: `Failed to update user password: ${(error as Error).message}`, level: 'error' });
      if (error instanceof HttpErrorResponse) {
        throw new AuthError(error.error?.data?.code);
      }
      throw new AuthError();
    }
  }
}
